import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';

export type BranchButtonSize = 'sm' | 'md' | 'lg';

@Component({
    selector: 'platform-branch-button',
    standalone: true,
    imports: [],
    template: `
        <button
            type="button"
            class="branch-button"
            [class.branch-button--sm]="size() === 'sm'"
            [class.branch-button--md]="size() === 'md'"
            [class.branch-button--lg]="size() === 'lg'"
            (click)="buttonClick.emit($event)"
            [attr.aria-label]="ariaLabel()"
        >
            <i class="pi pi-share-alt branch-button__icon"></i>
            <span class="branch-button__label">{{ label() }}</span>
            @if (showCaret()) {
                <i class="pi pi-caret-down branch-button__caret"></i>
            }
        </button>
    `,
    styles: [
        `
            .branch-button {
                display: flex;
                align-items: center;
                border-radius: 2px;
                background-color: #dcfce7;
                color: #166534;
                border: none;
                cursor: pointer;
                font-family: inherit;
            }

            .branch-button:hover {
                background-color: #bbf7d0;
            }

            .branch-button:focus-visible {
                outline: 2px solid #3ccb7f;
                outline-offset: 2px;
            }

            /* Size: Small */
            .branch-button--sm {
                gap: 6px;
                padding: 4px 8px;
            }

            .branch-button--sm .branch-button__icon {
                font-size: 8px;
            }

            .branch-button--sm .branch-button__label {
                font-size: 12px;
                font-weight: 500;
                max-width: 25rem;
            }

            .branch-button--sm .branch-button__caret {
                font-size: 10px;
            }

            /* Size: Medium (default) */
            .branch-button--md {
                gap: 8px;
                padding: 6px 12px;
            }

            .branch-button--md .branch-button__icon {
                font-size: 14px;
            }

            .branch-button--md .branch-button__label {
                font-size: 14px;
                font-weight: 500;
                max-width: 25rem;
            }

            .branch-button--md .branch-button__caret {
                font-size: 12px;
            }

            /* Size: Large */
            .branch-button--lg {
                gap: 12px;
                padding: 10px 16px;
            }

            .branch-button--lg .branch-button__icon {
                font-size: 16px;
            }

            .branch-button--lg .branch-button__label {
                font-size: 16px;
                font-weight: 500;
                max-width: 25rem;
            }

            .branch-button--lg .branch-button__caret {
                font-size: 14px;
            }

            /* Common styles */
            .branch-button__icon,
            .branch-button__caret {
                color: #166534;
                line-height: 1;
            }

            .branch-button__label {
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                line-height: 1;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BranchButtonComponent {
    /** Branch name to display */
    readonly label = input.required<string>();

    /** Button size variant */
    readonly size = input<BranchButtonSize>('md');

    /** Show dropdown caret icon */
    readonly showCaret = input<boolean>(false);

    /** Aria label for accessibility */
    readonly ariaLabel = input<string>('Change branch');

    /** Emitted when button is clicked */
    readonly buttonClick = output<MouseEvent>();
}
